/**
 * Centralized Date/Time Formatting Utilities
 * Uses 'en-IN' locale for consistent date formatting across the CRM
 */

const LOCALE = 'en-IN';
const TIMEZONE = 'Asia/Kolkata';

/**
 * Format date for display (e.g., "02 Jan 2026")
 * @param {Date|string} date - Date to format
 * @returns {string} Formatted date string
 */
export const formatDate = (date) => {
  if (!date) return '-';
  try {
    const d = new Date(date);
    if (isNaN(d.getTime())) return '-';
    return d.toLocaleDateString(LOCALE, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      timeZone: TIMEZONE,
    });
  } catch {
    return '-';
  }
};

/**
 * Format date with weekday (e.g., "Thu, 02 Jan 2026")
 * @param {Date|string} date - Date to format
 * @returns {string} Formatted date string with weekday
 */
export const formatDateWithWeekday = (date) => {
  if (!date) return '-';
  try {
    const d = new Date(date);
    if (isNaN(d.getTime())) return '-';
    return d.toLocaleDateString(LOCALE, {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      timeZone: TIMEZONE,
    });
  } catch {
    return '-';
  }
};

/**
 * Format time (e.g., "2:30 PM")
 * @param {Date|string} date - Date/time to format
 * @returns {string} Formatted time string
 */
export const formatTime = (date) => {
  if (!date) return '-';
  try {
    const d = new Date(date);
    if (isNaN(d.getTime())) return '-';
    return d.toLocaleTimeString(LOCALE, {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: TIMEZONE,
    });
  } catch {
    return '-';
  }
};

/**
 * Format date and time together (e.g., "02 Jan 2026, 2:30 PM")
 * @param {Date|string} date - Date/time to format
 * @returns {string} Formatted date and time string
 */
export const formatDateTime = (date) => {
  if (!date) return '-';
  try {
    const d = new Date(date);
    if (isNaN(d.getTime())) return '-';
    return d.toLocaleString(LOCALE, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: TIMEZONE,
    });
  } catch {
    return '-';
  }
};

/**
 * Format date and time with weekday (e.g., "Thu, 02 Jan 2026, 2:30 PM")
 * @param {Date|string} date - Date/time to format
 * @returns {object} Object with date and time properties
 */
export const formatDateTimeSeparate = (date) => {
  if (!date) return { date: '-', time: '-' };
  try {
    const d = new Date(date);
    if (isNaN(d.getTime())) return { date: '-', time: '-' };
    return {
      date: d.toLocaleDateString(LOCALE, {
        weekday: 'short',
        day: '2-digit',
        month: 'short',
        timeZone: TIMEZONE,
      }),
      time: d.toLocaleTimeString(LOCALE, {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: TIMEZONE,
      }),
    };
  } catch {
    return { date: '-', time: '-' };
  }
};

/**
 * Format relative date (e.g., "Today", "Yesterday", "2 days ago")
 * @param {Date|string} date - Date to format
 * @returns {string} Relative date string
 */
export const formatRelativeDate = (date) => {
  if (!date) return '-';
  try {
    const d = new Date(date);
    if (isNaN(d.getTime())) return '-';
    
    const now = new Date();
    const diffTime = now.getTime() - d.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    
    return formatDate(date);
  } catch {
    return '-';
  }
};

/**
 * Check if a date is today
 * @param {Date|string} date - Date to check
 * @returns {boolean}
 */
export const isToday = (date) => {
  if (!date) return false;
  try {
    const d = new Date(date);
    const today = new Date();
    return d.toDateString() === today.toDateString();
  } catch {
    return false;
  }
};

/**
 * Check if a date is overdue (past date)
 * @param {Date|string} date - Date to check
 * @returns {boolean}
 */
export const isOverdue = (date) => {
  if (!date) return false;
  try {
    const d = new Date(date);
    const now = new Date();
    // Set time to start of day for comparison
    d.setHours(23, 59, 59, 999);
    return d < now;
  } catch {
    return false;
  }
};

/**
 * Format date for HTML date input (YYYY-MM-DD)
 * @param {Date|string} date - Date to format
 * @returns {string} ISO date string for input
 */
export const toInputDate = (date) => {
  if (!date) return '';
  try {
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    return d.toISOString().split('T')[0];
  } catch {
    return '';
  }
};

/**
 * Format time for HTML time input (HH:MM)
 * @param {Date|string} date - Date/time to format
 * @returns {string} Time string for input
 */
export const toInputTime = (date) => {
  if (!date) return '';
  try {
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    return d.toTimeString().slice(0, 5);
  } catch {
    return '';
  }
};

/**
 * Combine date and time strings into a Date object
 * @param {string} dateStr - Date string (YYYY-MM-DD)
 * @param {string} timeStr - Time string (HH:MM)
 * @returns {Date|null}
 */
export const combineDateAndTime = (dateStr, timeStr) => {
  if (!dateStr) return null;
  try {
    const combined = timeStr ? `${dateStr}T${timeStr}:00` : `${dateStr}T00:00:00`;
    const d = new Date(combined);
    return isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
};

export default {
  formatDate,
  formatDateWithWeekday,
  formatTime,
  formatDateTime,
  formatDateTimeSeparate,
  formatRelativeDate,
  isToday,
  isOverdue,
  toInputDate,
  toInputTime,
  combineDateAndTime,
};
